"use client";

import { AudioWaveform, FileText, Radio, ArrowRight, PartyPopper } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  title: "Accompagnement postnatal à domicile",
  description: "Un soutien bienveillant et personnalisé pour vous accompagner dès l'accouchement. Je veille à votre rétablissement physique et émotionnel avec douceur et expertise.",
  capabilities: ["TEMPS POUR SOI", "Aide à la maison", "Soutien aux aînés", "RSERRAGE DU BASSIN", "Soins corporels", "entre autres"],
  Icon: AudioWaveform,
  link: {
    to: "/AccommodationsPage"
  }
}, {
  title: "Batch Cooking à domicile",
  description: "Des repas sains, nutritifs et réconfortants préparés chez vous. Je privilégie des ingrédients de saison pour soutenir votre énergie et votre récupération après l'accouchement.",
  capabilities: ["Cuisine sur mesure", "SOUTIEN À LA MAISON", "Nutrition ciblée"],
  Icon: Radio,
  link: {
    to: "/BatchCookingPage"
  }
}, {
  title: "Cercle Maman-Bébé 0-4 mois",
  description: "Pendant 6 semaines, un petit groupe de mamans se retrouve autour d’expertes périnatales pour échanger, poser ses questions et repartir avec des outils concrets, dans un cadre doux et bienveillant.",
  capabilities: ["Cercles de parole", "Ateliers thématiques", "RENCONTRES ENTRE MAMANS"],
  Icon: FileText,
  link: {
    to: "/CirclePage"
  }
}, {
  title: "Nesting Party",
  description: "Un moment chaleureux où ton village se réunit pour préparer concrètement l'arrivée de bébé : repas prêts à réchauffer, affaires de bébé organisées, essentiels à portée de main. Pour avancer ensemble et te sentir entourée pour la suite.",
  capabilities: ["Repas préparés", "Organisation bébé", "Entourage mobilisé"],
  Icon: PartyPopper,
  link: {
    to: "/NestingPartyPage"
  }
}];
function PillarCard({
  pillar,
  index
}: {
  pillar: typeof pillars[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-index={index} ref={ref} className={`group bg-[#FCFAF7] border border-[#D5C3B1]/30 shadow-sm rounded-lg p-8 md:p-10 flex flex-col items-center text-center gap-5 md:gap-6 h-full transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      {/* Icon Medallion */}
      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 border border-primary/30 text-primary flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:scale-105">
        <pillar.Icon className="size-8 md:size-9 text-primary" />
      </div>

      <h3 className="font-serif font-normal text-2xl md:text-3xl text-[#43362F]">
        {pillar.title}
      </h3>

      <p className="text-base md:text-lg text-[#43362F]/75 leading-relaxed">
        {pillar.description}
      </p>

      {/* Capabilities rendered with interpunct separator */}
      <div className="flex flex-wrap items-center justify-center text-center font-sans text-xs md:text-sm uppercase tracking-[0.15em] text-[#43362F]/50 my-1 leading-relaxed">
        {pillar.capabilities.map((cap, idx) => <span key={cap} data-index={idx} className="inline-flex items-center">
            {idx > 0 && <span className="mx-1.5 text-primary/60 select-none">·</span>}
            <span>{cap}</span>
          </span>)}
      </div>

      <Button asChild variant="outline" className="border border-primary/70 hover:border-primary bg-transparent text-[#43362F] hover:bg-primary hover:text-primary-foreground text-sm font-sans uppercase tracking-[0.18em] rounded-lg h-auto px-7 py-3 transition-all duration-300 mt-auto">
        <Link to={pillar.link.to} className="inline-flex items-center gap-2">
          <span>En savoir plus</span>
          <ArrowRight className="size-3.5" />
        </Link>
      </Button>
    </div>;
}
export default function Expertise() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5196" id="un-soutien-expert-là-où-vous-en-avez-besoin" className="bg-[#43362F] text-[#FCFAF7] py-10 px-4 md:py-14 lg:py-16 md:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header: 2-column grid */}
        <div ref={intro.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-primary">
                Mes services
              </p>
              <div className="w-10 h-px bg-primary" aria-hidden="true" />
            </div>
            <h2 className="font-serif font-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter text-[#FCFAF7]">
              Mes Services
            </h2>
          </div>

          <div className="flex flex-col justify-center">
            <p className="text-base sm:text-lg text-[#FCFAF7]/80 leading-relaxed mb-6 font-sans">
              En individuel ou en groupe, je t’accompagne avec douceur et bienveillance.
              <br />
              Mes services à domicile sont entièrement personnalisables selon tes besoins et tes envies.
            </p>
            <Link to="/ContactPage" className="group inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-[#FCFAF7] underline hover:text-primary transition-colors cursor-pointer w-fit">
              <span>ME CONTACTER</span>
              <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* 2-Column Grid on Desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10 mt-12">
          {pillars.map((pillar, i) => <PillarCard key={pillar.title} pillar={pillar} index={i} />)}
        </div>

        {/* Mobile bottom CTA */}
        <div className="lg:hidden flex justify-center pt-8 mt-4">
          <Button asChild variant="outline" className="border border-primary/70 hover:border-primary bg-transparent text-[#FCFAF7] hover:bg-primary hover:text-primary-foreground font-sans text-sm uppercase tracking-[0.2em] rounded-lg h-auto py-3 px-8 transition-colors duration-300">
            <Link to="/AccommodationsPage" className="inline-flex items-center gap-2">
              <span>Découvrir nos services</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}